import { useParams, Navigate, Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, CheckCircle2, Star, Award, Users, Clock } from "lucide-react";
import { CITIES, BOARDS, COURSES } from "../data/siteData";
import { OFFLINE_LOCALITIES as OFFLINE_LOCALITIES_STATIC } from "../data/offlineLocalityData";
import RegistrationForm from "../components/RegistrationForm";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import WhyChooseAnandTutorials from "../components/WhyChooseAnandTutorials";
import RelatedCityBlogs from "../components/RelatedCityBlogs";
import { QuickAnswerCard, DefinitionBox, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { buildCityAEO } from "../data/aeoContent";
import {
    CLASS_COVERAGE, CURRICULUM_COVERAGE, SUBJECT_COVERAGE,
    ENTRANCE_EXAM_COVERAGE, SEARCH_INTENT_COVERAGE,
} from "../data/seoCoverage";
import { useGbpForCity } from "../lib/useGbp";
import { trackCTA } from "../lib/analytics";

export default function CityLanding({ citySlug, aliasPath }) {
    const params = useParams();
    const city = citySlug || params.city;
    const data = CITIES[city];

    // Always-call hooks first (rules-of-hooks); we'll do the early-return after.
    const { profile: gbp } = useGbpForCity(data ? data.slug : null);

    if (!data) return <Navigate to="/" replace />;

    // Live GBP — DB is the single source of truth (no static fallback). When
    // admin sets active=false for a city, the public API hides it, gbp is
    // null, and we render NO CTA + no sameAs/identifier link for that city.
    // Re-activate via admin and the page picks it up on next load.
    const gbpUrl = gbp ? (gbp.business_url || gbp.map_url || gbp.review_url || "") : "";
    const gbpKgmid = gbp?.kgmid || "";
    const gbpShown = Boolean(gbp && gbp.active && gbpUrl);

    const canonicalPath = `/${data.slug}`;
    const renderedPath = aliasPath || canonicalPath;
    const canonicalUrl = `https://anandtutorials.com${canonicalPath}`;

    // 1) LocalBusiness — branch + GEO + areaServed
    const localBusinessSchema = {
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "EducationalOrganization"],
        "name": `Anand Tutorials ${data.name}`,
        "alternateName": [
            `Anand Tutorials — ${data.name}`,
            `Anand Tutorials in ${data.name}`,
            `Anand Tutorials ${data.name} Branch`,
            `Home Tuition ${data.name}`,
            `Home Tutors ${data.name}`,
        ],
        "image": "https://anandtutorials.com/logo-anand-tutorials.png",
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
        "@id": `${canonicalUrl}#localbusiness`,
        "url": canonicalUrl,
        "telephone": data.phone,
        "email": data.email,
        "branchOf": {
            "@type": "EducationalOrganization",
            "name": "Anand Tutorials",
            "url": "https://anandtutorials.com",
        },
        "knowsAbout": [
            `Home Tuition in ${data.name}`,
            `Home Tutor in ${data.name}`,
            `Online Tuition in ${data.name}`,
            `1:1 Live Online Classes in ${data.name}`,
            `CBSE Tutor in ${data.name}`,
            `ICSE Tutor in ${data.name}`,
            `${data.state.includes("Madhya") ? "MP" : data.state} Board Tutor in ${data.name}`,
            `NEET Coaching in ${data.name}`,
            `JEE Coaching in ${data.name}`,
            `Female Home Tutor in ${data.name}`,
            `Private Tutor in ${data.name}`,
            // V172 — Region-specific entities (MHT-CET for MH, MPPSC for MP, etc.)
            // appended from the per-city regionalKnowsAbout seed so Google can
            // link the branch to its state board / regional authority entities.
            ...(data.regionalKnowsAbout || []),
        ],
        "address": {
            "@type": "PostalAddress",
            "streetAddress": data.address,
            "addressLocality": data.name,
            "addressRegion": data.state,
            "postalCode": data.postalCode,
            "addressCountry": data.countryCode || "IN",
        },
        ...(data.geo && data.geo.lat && data.geo.lng ? {
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": data.geo.lat,
                "longitude": data.geo.lng,
            },
        } : {}),
        "areaServed": data.areas.map((a) => ({ "@type": "Place", "name": `${a}, ${data.name}` })),
        "aggregateRating": {
            "@type": "AggregateRating",
            // V172 — Each branch declares its OWN rating so Google reads a
            // city-specific trust signal (Nagpur 4.9/680, Bhopal 4.9/720,
            // Jabalpur 4.9/850). Previously all three cities shared a
            // hardcoded 4.8/850 which weakened the city-level distinction.
            "ratingValue": (data.rating && data.rating.value) || "4.8",
            "reviewCount": (data.rating && data.rating.count) || "850",
            "bestRating": "5",
            "worstRating": "1",
        },
        "openingHoursSpecification": [{
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "09:00",
            "closes": "21:00",
        }],
        // Google Business Profile entity-linking — most important explicit
        // signal we can give Google that this LocalBusiness on the website is
        // the SAME entity as the city's verified Google Business listing. Maps
        // the schema entity to the Knowledge Graph (kgmid) and includes the
        // public share URL in sameAs.
        ...(gbpKgmid ? {
            "identifier": [{
                "@type": "PropertyValue",
                "propertyID": "googleKgmid",
                "value": gbpKgmid,
            }],
        } : {}),
        "sameAs": [
            "https://www.facebook.com/anandtutorialsofficial",
            "https://www.instagram.com/anandtutorialsofficial",
            gbpShown ? gbpUrl : null,
            // V172 — Per-city external directory citations (JustDial / UrbanPro /
            // Sulekha). Each city's citations are declared in cities.js. This is
            // the single strongest off-site entity-linking signal we can emit
            // from code — tells Google "this LocalBusiness is the same entity
            // listed on these authority directories for this city".
            ...((data.externalCitations || []).filter(Boolean)),
        ].filter(Boolean),
    };

    // 2) Organisation — root brand identity (city-page-level reference)
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "@id": "https://anandtutorials.com#organization",
        "name": "Anand Tutorials",
        "url": "https://anandtutorials.com",
        "logo": "https://anandtutorials.com/logo-anand-tutorials.png",
        "foundingDate": "2013",
        "founders": [{ "@type": "Person", "name": "Anand Rajput" }],
        "sameAs": [
            "https://www.facebook.com/anandtutorialsofficial",
            "https://www.instagram.com/anandtutorialsofficial",
        ],
    };

    // 3) WebPage — page-level identity, ties everything back to canonical URL
    const webPageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        "url": canonicalUrl,
        "name": data.seoTitle,
        "description": data.seoDescription,
        "inLanguage": "en-IN",
        "isPartOf": { "@id": "https://anandtutorials.com#organization" },
        "about": { "@id": `${canonicalUrl}#localbusiness` },
        "primaryImageOfPage": {
            "@type": "ImageObject",
            "url": "https://anandtutorials.com/logo-anand-tutorials.png",
        },
        "breadcrumb": { "@id": `${canonicalUrl}#breadcrumb` },
        // V64 — Speakable helps Google Assistant / voice search identify TTS-ready
        // sections. Points at the H1 hero + FAQ list so answer engines can quote them.
        "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "[data-speakable]", "details summary", "details > div"],
        },
    };

    // 4) BreadcrumbList — Home › Online Tuition hub › <City>
    // V172 — Position 2 now points at /online-classes (the real hub) instead
    // of back to /, so Google sees a 3-step hierarchical path (not a loop).
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "Home Tuition", "item": "https://anandtutorials.com/online-classes" },
            { "@type": "ListItem", "position": 3, "name": data.name, "item": canonicalUrl },
        ],
    };

    // 5) FAQPage — city-specific FAQs (voice-search & rich-result eligible)
    const faqSchema = data.faqs && data.faqs.length ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${canonicalUrl}#faq`,
        "mainEntity": data.faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    } : null;

    // 6) Service — tuition service offered in this city
    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonicalUrl}#service`,
        "serviceType": `Home Tuition & 1:1 Online Classes in ${data.name}`,
        "provider": { "@id": "https://anandtutorials.com#organization" },
        "areaServed": {
            "@type": "City",
            "name": data.name,
            "containedInPlace": { "@type": "AdministrativeArea", "name": data.state },
        },
        "audience": { "@type": "PeopleAudience", "audienceType": "Students Grade 1-12 + NEET / JEE aspirants" },
        "category": "Educational Services",
        "url": canonicalUrl,
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": `Tuition services in ${data.name}`,
            "itemListElement": [
                "1:1 Home Tuition", "1:1 Live Online Classes", "CBSE Tuition", "ICSE Tuition",
                // V172 — Use the city's real state-board label so Google sees
                // "Maharashtra State Board (MSBSHSE — SSC/HSC) Tuition" for
                // Nagpur instead of the previous generic "State Board Tuition".
                data.stateBoardSchemaName || `${data.state.includes("Madhya") ? "MP" : "State"} Board Tuition`,
                "NEET Coaching", "JEE Coaching",
                "Spoken English", "IELTS / TOEFL", "Coding",
            ].map((n) => ({ "@type": "Offer", "itemOffered": { "@type": "Service", "name": n } })),
        },
    };

    // V65 — AEO / GEO content (Quick Answer + Definition + Conversational FAQ)
    const cityAEO = buildCityAEO(data);

    return (
        <div data-testid={`city-page-${data.slug}`}>
            <SEO
                title={data.seoTitle}
                description={data.seoDescription}
                keywords={data.keywords}
                path={renderedPath}
                canonicalPath={canonicalPath}
                geo={{
                    region: data.regionCode || "IN-MP",
                    placename: `${data.name}, ${data.state}`,
                    lat: data.geo && data.geo.lat,
                    lng: data.geo && data.geo.lng,
                }}
                schemas={[
                    localBusinessSchema,
                    organizationSchema,
                    webPageSchema,
                    breadcrumbSchema,
                    serviceSchema,
                    faqSchema,
                    buildConversationalFAQSchema(cityAEO.faqs),
                ]}
            />

            <Breadcrumbs items={[
                { name: "Home Tuition", to: "/" },
                { name: data.name },
            ]} />

            <PageHeader
                eyebrow={`Home Tuition · ${data.state}`}
                title={data.h1}
                subtitle={data.intro}
            >
                <div className="flex flex-wrap gap-3">
                    <a href={`tel:${data.phoneRaw}`} onClick={() => trackCTA("call_now_clicked", { cta_location: `city-${data.slug}-hero` })} data-testid={`city-${data.slug}-call`} data-phone-key={data.slug} className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        <Phone className="w-4 h-4" /> Call {data.name} Team
                    </a>
                    <Link to="/register" onClick={() => trackCTA("book_demo_clicked", { cta_location: `city-${data.slug}-hero` })} data-testid={`city-${data.slug}-demo`} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15">
                        Book Free Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    {gbpShown && (
                        <a
                            href={gbpUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`city-${data.slug}-gbp`}
                            aria-label={`View Anand Tutorials ${data.name} on Google`}
                            className="inline-flex items-center gap-2 bg-white text-[#0F172A] font-semibold px-5 py-3 rounded-lg border border-white/30 hover:bg-slate-50 transition-colors"
                        >
                            <span className="font-bold text-blue-600">G</span>
                            <span className="text-[#EA4335] -ml-1.5 font-bold">o</span>
                            <span className="text-amber-500 -ml-1.5 font-bold">o</span>
                            <span className="text-blue-600 -ml-1.5 font-bold">g</span>
                            <span className="text-emerald-600 -ml-1.5 font-bold">l</span>
                            <span className="text-[#EA4335] -ml-1.5 font-bold">e</span>
                            <span className="text-slate-700 ml-1">— View us on Google</span>
                        </a>
                    )}
                </div>
            </PageHeader>

            {/* V65 — AEO Quick-Answer card (top-of-page TL;DR for AI Overviews / ChatGPT / Perplexity) */}
            <QuickAnswerCard
                question={cityAEO.quickAnswer.question}
                answer={cityAEO.quickAnswer.answer}
                bullets={cityAEO.quickAnswer.bullets}
                footer={cityAEO.quickAnswer.footer}
                testId={`city-${data.slug}-quick-answer`}
            />

            {/* Stats trust strip */}
            <section className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    {[
                        { i: Users, n: "8,000+", l: `Students in ${data.name}` },
                        { i: Star, n: "4.9 / 5", l: "Parent rating" },
                        { i: Award, n: "98%", l: "Pass rate (boards)" },
                        { i: Clock, n: "24 hrs", l: "Tutor match time" },
                    ].map(({ i: I, n, l }) => (
                        <div key={l}>
                            <I className="w-6 h-6 text-[#1E3A8A] mx-auto mb-2" />
                            <div className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">{n}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Branch info card */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">{data.name} Branch</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">Visit our {data.name} office — or we'll come to you.</h2>
                        <div className="space-y-4 text-slate-700">
                            <div className="flex gap-3"><MapPin className="w-5 h-5 mt-1 text-[#1E3A8A] shrink-0" /><span><strong>Address:</strong> {data.address}</span></div>
                            <div className="flex gap-3"><Phone className="w-5 h-5 mt-1 text-[#1E3A8A] shrink-0" /><a href={`tel:${data.phoneRaw}`} data-phone-key={data.slug} data-testid={`city-branch-call-${data.slug}`} className="hover:text-[#1E3A8A]"><strong>Call:</strong> {data.name} Team</a></div>
                            <div className="flex gap-3"><Mail className="w-5 h-5 mt-1 text-[#1E3A8A] shrink-0" /><a href={`mailto:${data.email}`} className="hover:text-[#1E3A8A] break-all"><strong>Email:</strong> {data.email}</a></div>
                            {data.mapUrl && (
                                <div className="pt-1">
                                    <a
                                        href={data.mapUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`city-branch-directions-${data.slug}`}
                                        aria-label={`Get directions to Anand Tutorials ${data.name} branch on Google Maps`}
                                        className="inline-flex items-center gap-2 bg-[#1E3A8A] text-white font-semibold px-4 py-2.5 rounded-lg hover:bg-[#1E40AF] transition-colors text-sm"
                                    >
                                        <MapPin className="w-4 h-4" /> Get Directions on Google Maps
                                    </a>
                                </div>
                            )}
                        </div>
                        <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200" data-testid={`city-branch-map-${data.slug}`}>
                            <iframe
                                title={`Anand Tutorials ${data.name} Branch — ${data.address}`}
                                src={data.mapEmbed}
                                width="100%"
                                height="320"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                            />
                        </div>
                    </div>
                    <div>
                        <RegistrationForm defaultCity={data.name} title={`Book Demo in ${data.name}`} />
                    </div>
                </div>
            </section>

            {/* SEO content sections */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-10">
                    {data.seoSections.map((s, i) => (
                        <article key={i}>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4 text-[#0F172A]">{s.h2}</h2>
                            <p className="text-slate-700 leading-relaxed text-base sm:text-lg">{s.p}</p>
                        </article>
                    ))}
                </div>
            </section>

            {/* WHY CHOOSE — EEAT block */}
            <WhyChooseAnandTutorials
                eyebrow={`Why ${data.name} parents trust us`}
                headline={`Why Anand Tutorials is ${data.name}'s most trusted 1:1 tuition brand.`}
                contextLabel={`in ${data.name} and pan-India online`}
            />

            {/* Areas served — clickable hyperlocal entry-points (CBSE home tuition + ?area=) */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Dedicated Locality Pages</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Home tuition — pick your {data.name} locality</h2>
                    <p className="text-slate-600 mb-8 max-w-3xl">Top {data.name} localities each have a dedicated Home Tuition page with locality-specific tutors, schools and NEET / JEE mentors. Tap yours below.</p>
                    <div className="grid sm:grid-cols-3 md:grid-cols-5 gap-3 mb-10">
                        {(OFFLINE_LOCALITIES_STATIC[data.slug] || []).map((loc) => (
                            <Link
                                key={loc.slug}
                                to={`/home-tuition-${loc.slug}-${data.slug}`}
                                data-testid={`city-locality-page-${loc.slug}`}
                                aria-label={`Home tuition in ${loc.name}, ${data.name}`}
                                className="group bg-[#F8FAFC] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 flex items-center gap-2 hover:border-[#1E3A8A] hover:bg-white hover:shadow-md transition-all"
                            >
                                <MapPin className="w-4 h-4 text-[#1E3A8A] shrink-0" />
                                <span className="group-hover:text-[#1E3A8A] font-semibold">Home tuition · {loc.name}</span>
                            </Link>
                        ))}
                    </div>

                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">All Localities Served</div>
                    <h3 className="font-heading text-xl sm:text-2xl font-bold mb-3">Verified tutors across every {data.name} area</h3>
                    <p className="text-slate-600 mb-8 max-w-3xl">If your area isn&apos;t in the dedicated pages above, tap below for the CBSE hyperlocal tutor entry.</p>
                    <div className="grid sm:grid-cols-3 md:grid-cols-5 gap-3">
                        {data.areas.map((a) => (
                            <Link
                                key={a}
                                to={`/cbse-home-tuition-${data.slug}?area=${encodeURIComponent(a)}`}
                                data-testid={`city-area-link-${a.toLowerCase().replace(/\s+/g, "-")}`}
                                aria-label={`Home tuition in ${a}, ${data.name}`}
                                className="group bg-[#F8FAFC] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 flex items-center gap-2 hover:border-[#1E3A8A] hover:bg-white hover:shadow-md transition-all"
                            >
                                <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                                <span className="group-hover:text-[#1E3A8A] font-medium">Home tuition in {a}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Boards */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">All Boards in {data.name}</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">Specialist tutors for every curriculum</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {BOARDS.map((b) => (
                            <div key={b.code} className="bg-white rounded-xl border border-slate-200 p-5">
                                <h3 className="font-heading font-semibold text-[#1E3A8A] mb-1">{b.name} tutor in {data.name}</h3>
                                <p className="text-xs text-slate-500 leading-relaxed">{b.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* V93 — Complete SEO coverage grids.
                Additive-only: adds crawlable keyword coverage + internal
                links for every grade / curriculum / subject / entrance-exam
                intent without touching the existing Boards / Popular
                Programs / Areas sections above. Renders as clean chip
                grids matching the existing design vocabulary. */}
            <section className="py-16 bg-white border-t border-slate-100" data-testid={`city-${data.slug}-coverage`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-12">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Every Grade Covered</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Home tuition in {data.name} — from Nursery to Class 12</h2>
                        <p className="text-slate-600 mb-6 max-w-3xl">Verified 1:1 home tutors and live online tutors in {data.name} for every stage of school — Nursery, LKG, UKG, Kindergarten, Preschool, Primary Classes, Middle School, Secondary School and Higher Secondary. Every class handled by a subject specialist.</p>
                        <div className="flex flex-wrap gap-2">
                            {CLASS_COVERAGE.map((c) => (
                                <Link
                                    key={c.slug}
                                    to="/courses"
                                    data-testid={`city-${data.slug}-class-${c.slug}`}
                                    aria-label={`${c.label} home tuition in ${data.name}`}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F8FAFC] border border-slate-200 rounded-full text-sm text-slate-700 hover:border-[#1E3A8A] hover:bg-white hover:text-[#1E3A8A] transition-colors"
                                >
                                    {c.label} home tuition in {data.name}
                                </Link>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Every Curriculum</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Board & international curriculum coverage in {data.name}</h2>
                        <p className="text-slate-600 mb-6 max-w-3xl">CBSE, ICSE, ISC, {data.slug === "nagpur" ? "Maharashtra Board" : "MP Board"}, State Board, NIOS plus all international curricula — IGCSE, IB, Cambridge, Edexcel, A Levels, GCSE, AP, American and British curriculum. Every {data.name} tutor is matched to your exact curriculum.</p>
                        <div className="flex flex-wrap gap-2">
                            {CURRICULUM_COVERAGE.map((b) => (
                                <span
                                    key={b}
                                    data-testid={`city-${data.slug}-curr-${b.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F8FAFC] border border-slate-200 rounded-full text-sm text-slate-700"
                                >
                                    {b} tutor in {data.name}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Every Subject</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Subject-specialist home tutors in {data.name}</h2>
                        <p className="text-slate-600 mb-6 max-w-3xl">From core academics (Mathematics, Science, English, Hindi, Sanskrit, Social Science) to commerce (Accountancy, Business Studies, Economics, Statistics), science (Physics, Chemistry, Biology), computer science (Java, Python, C++, Artificial Intelligence, Coding), languages (Spoken English, IELTS, TOEFL, PTE, French, German, Spanish, Japanese) and skills (Vedic Maths, Abacus, Handwriting Improvement, Chess, Drawing, Music) — every subject has a specialist tutor in {data.name}.</p>
                        <div className="flex flex-wrap gap-2">
                            {SUBJECT_COVERAGE.map((s) => (
                                <span
                                    key={s}
                                    data-testid={`city-${data.slug}-subj-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F8FAFC] border border-slate-200 rounded-full text-sm text-slate-700"
                                >
                                    {s} tutor in {data.name}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Entrance Exam Coaching</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">1:1 entrance exam coaching in {data.name}</h2>
                        <p className="text-slate-600 mb-6 max-w-3xl">JEE Main, JEE Advanced, NEET, IIT Foundation, Foundation Classes, Olympiads, NTSE, KVPY, CUET, CLAT, NDA, SSC, Banking, UPSC Foundation, and Polytechnic — 1:1 mentorship by exam-specialist tutors in {data.name}.</p>
                        <div className="flex flex-wrap gap-2">
                            {ENTRANCE_EXAM_COVERAGE.map((e) => (
                                <span
                                    key={e}
                                    data-testid={`city-${data.slug}-exam-${e.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F8FAFC] border border-slate-200 rounded-full text-sm text-slate-700"
                                >
                                    {e} coaching in {data.name}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Every Search Intent</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Whatever you need — {data.name} 1:1 tuition covers it</h2>
                        <p className="text-slate-600 mb-6 max-w-3xl">Best home tuition, affordable home tuition, premium home tuition, doorstep tuition, offline tuition, home coaching, personalized tuition, individual tuition, one-to-one tuition, weekend tuition, daily tuition, morning tuition, evening tuition, crash courses, board exam preparation, doubt solving, homework help, assignment help, revision classes and test preparation — every home tuition search intent in {data.name} maps to a verified Anand Tutorials tutor.</p>
                        <div className="flex flex-wrap gap-2">
                            {SEARCH_INTENT_COVERAGE.map((p) => (
                                <span
                                    key={p}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F8FAFC] border border-slate-200 rounded-full text-sm text-slate-700"
                                >
                                    {p} in {data.name}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Popular programs (internal links to /courses) */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Programs in {data.name}</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">Popular home tuition programs in {data.name}</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {COURSES.slice(0, 4).map((c) => (
                            <Link key={c.title} to="/courses" className="bg-[#F8FAFC] border border-slate-200 rounded-xl p-5 hover:border-blue-200 hover:shadow-md transition-all">
                                <h3 className="font-heading font-semibold text-[#1E3A8A] mb-1">{c.title} in {data.name}</h3>
                                <p className="text-xs text-slate-500 leading-relaxed">{c.desc}</p>
                            </Link>
                        ))}
                    </div>
                    <div className="mt-6 flex flex-wrap gap-3 text-sm">
                        <Link to="/courses" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">All courses <ArrowRight className="w-4 h-4" /></Link>
                        <span className="text-slate-300">·</span>
                        <Link to="/online-classes" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">Online classes <ArrowRight className="w-4 h-4" /></Link>
                        <span className="text-slate-300">·</span>
                        <Link to="/blog" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">Read tutoring tips <ArrowRight className="w-4 h-4" /></Link>
                        <span className="text-slate-300">·</span>
                        <Link to="/become-a-tutor" className="inline-flex items-center gap-1 text-[#1E3A8A] font-semibold hover:gap-2 transition-all">Teach with us <ArrowRight className="w-4 h-4" /></Link>
                    </div>
                </div>
            </section>

            {/* Other cities */}
            <section className="py-12 bg-[#F8FAFC] border-t border-slate-200">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-2">Also Available In</div>
                    <h3 className="font-heading text-xl font-semibold mb-4">Anand Tutorials home tuition in other cities</h3>
                    <div className="flex flex-wrap justify-center gap-3">
                        {Object.values(CITIES).filter((c) => c.slug !== data.slug).map((c) => (
                            <Link key={c.slug} to={`/${c.slug}`} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-[#1E3A8A] hover:border-blue-200 hover:shadow-sm">
                                Home tuition in {c.name} →
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Related blog reads — city-targeted topical cluster */}
            <RelatedCityBlogs citySlug={data.slug} cityName={data.name} />

            {/* V65 — AEO Definition block for the city's home tuition entity */}
            <DefinitionBox
                term={cityAEO.definition.term}
                definition={cityAEO.definition.definition}
                examples={cityAEO.definition.examples}
                testId={`city-${data.slug}-definition`}
            />

            {/* V65 — AEO Conversational FAQ (voice-search + LLM ingestion) */}
            <ConversationalFAQ
                heading={`Deeper questions about home tuition in ${data.name}`}
                subheading={`Long-form answers to the questions ${data.name} parents most often ask on our enquiry calls.`}
                items={cityAEO.faqs}
                testId={`city-${data.slug}-conversational-faq`}
            />

            {/* CTA strip */}
            <section className="py-16 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Start your child's success story in {data.name}.</h2>
                    <p className="text-blue-100 max-w-2xl mx-auto mb-6">Book a free 1:1 demo with a verified home tutor in {data.name}. No payment, no commitment — just see the difference.</p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a href={`tel:${data.phoneRaw}`} onClick={() => trackCTA("call_now_clicked", { cta_location: `city-${data.slug}-footer-cta` })} data-testid={`city-${data.slug}-footer-cta-call`} data-phone-key={data.slug} className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400">
                            <Phone className="w-4 h-4" /> Call {data.name} Team
                        </a>
                        <Link to="/register" onClick={() => trackCTA("book_demo_clicked", { cta_location: `city-${data.slug}-footer-cta` })} className="inline-flex items-center gap-2 bg-white text-[#1E3A8A] font-semibold px-6 py-3 rounded-lg hover:bg-slate-50">
                            Book Free Demo
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
